import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Bolt Torque Lookup — engineering reference table for tightening
// torques. Values are computed from standard formulas (T = K * d * F),
// not stored as a lookup. K (nut factor) ≈ 0.20 for plain dry threads,
// 0.18 for lubricated; we use 0.20 as the conservative default.
//
// Yield strength for each grade (from ASTM specs and ISO 898-1):
//   SAE Grade 2  : 57 ksi  / 393 MPa  (low-carbon, common hardware)
//   SAE Grade 5  : 92 ksi  / 634 MPa  (medium-carbon, heat treated)
//   SAE Grade 8  : 130 ksi / 896 MPa  (alloy, heat treated)
//   ISO 8.8      : 580 MPa            (medium-carbon, ~Grade 5 equiv)
//   ISO 10.9     : 830 MPa            (alloy, ~Grade 8 equiv)
//   ISO 12.9     : 1100 MPa           (high-strength alloy)
//   A2-70        : 450 MPa            (austenitic SS, ~stainless 304)
//
// Target preload = 0.75 × yield × stress area
// Torque = K × d × preload, with d the NOMINAL diameter.
//
// We compute both ft-lb and N·m, and show side by side.

const K_DEFAULT = 0.20;

const GRADES = {
  "Grade 2":  { yieldMPa: 393, label: "SAE Grade 2 (low-carbon, marked plain)" },
  "Grade 5":  { yieldMPa: 634, label: "SAE Grade 5 (medium, 3 radial lines)" },
  "Grade 8":  { yieldMPa: 896, label: "SAE Grade 8 (alloy, 6 radial lines)" },
  "ISO 8.8":  { yieldMPa: 580, label: "ISO 8.8 (medium-carbon)" },
  "ISO 10.9": { yieldMPa: 830, label: "ISO 10.9 (alloy)" },
  "ISO 12.9": { yieldMPa: 1100, label: "ISO 12.9 (high-strength)" },
  "A2-70":    { yieldMPa: 450, label: "A2-70 stainless (304/A2 austenitic)" },
};

// Stress areas from standard tables. Diameter in mm; stress area in mm²;
// also nominal diameter in inches and stress area in in².
const SIZES_METRIC = [
  // [name, diaMM, stressAreaMM2]
  ["M3",   3, 5.03],
  ["M4",   4, 8.78],
  ["M5",   5, 14.2],
  ["M6",   6, 20.1],
  ["M8",   8, 36.6],
  ["M10", 10, 58.0],
  ["M12", 12, 84.3],
  ["M14", 14, 115],
  ["M16", 16, 157],
  ["M20", 20, 245],
  ["M24", 24, 353],
];
const SIZES_IMPERIAL = [
  // [name, diaIn, stressAreaIn2]  (UNC coarse threads)
  ["1/4-20",  0.250, 0.0318],
  ["5/16-18", 0.3125, 0.0524],
  ["3/8-16",  0.375, 0.0775],
  ["7/16-14", 0.4375, 0.1063],
  ["1/2-13",  0.500, 0.1419],
  ["9/16-12", 0.5625, 0.182],
  ["5/8-11",  0.625, 0.226],
  ["3/4-10",  0.750, 0.334],
  ["7/8-9",   0.875, 0.462],
  ["1-8",     1.000, 0.606],
];

const computeTorque = ({ yieldMPa, sizeRow, system }) => {
  const targetPreloadFraction = 0.75;
  if (system === "metric") {
    const [, diaMM, stressMM2] = sizeRow;
    const preloadN = targetPreloadFraction * yieldMPa * stressMM2; // N
    const torqueNm = K_DEFAULT * (diaMM / 1000) * preloadN; // N·m
    const torqueFtLb = torqueNm * 0.7376;
    return { Nm: torqueNm, ftLb: torqueFtLb };
  } else {
    const [, diaIn, stressIn2] = sizeRow;
    // Convert yield MPa to psi: 1 MPa = 145.04 psi
    const yieldPsi = yieldMPa * 145.04;
    const preloadLbf = targetPreloadFraction * yieldPsi * stressIn2; // lbf
    const torqueInLb = K_DEFAULT * diaIn * preloadLbf; // in-lb
    const torqueFtLb = torqueInLb / 12;
    const torqueNm = torqueFtLb * 1.3558;
    return { Nm: torqueNm, ftLb: torqueFtLb };
  }
};

export default function BoltTorque() {
  const [grade, setGrade] = useState("Grade 5");
  const [system, setSystem] = useState("metric");
  const [size, setSize] = useState("M8");

  // Snap to a valid size when system changes
  const sizes = system === "metric" ? SIZES_METRIC : SIZES_IMPERIAL;
  if (!sizes.find((s) => s[0] === size)) {
    setTimeout(() => setSize(sizes[3][0]), 0);
  }
  // Filter grades by system (ISO/A2 are typically metric; SAE typically imperial)
  const compatibleGrades = system === "metric"
    ? ["ISO 8.8", "ISO 10.9", "ISO 12.9", "A2-70"]
    : ["Grade 2", "Grade 5", "Grade 8"];
  if (!compatibleGrades.includes(grade)) {
    setTimeout(() => setGrade(compatibleGrades[1] || compatibleGrades[0]), 0);
  }

  const result = useMemo(() => {
    const sizeRow = sizes.find((s) => s[0] === size);
    if (!sizeRow) return null;
    const g = GRADES[grade];
    if (!g) return null;
    return computeTorque({ yieldMPa: g.yieldMPa, sizeRow, system });
  }, [grade, size, system]);

  const sel = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <ToolsShell title="Bolt Torque">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Typical tightening torque for plain dry threads, sized at 75% of yield. <strong>Always defer to the equipment's specific torque spec when it exists.</strong>
      </p>

      <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
        {[{ id: "metric", label: "Metric (ISO)" }, { id: "imperial", label: "Imperial (SAE)" }].map((u) => (
          <button
            key={u.id}
            onClick={() => setSystem(u.id)}
            style={{ background: system === u.id ? TOOLS_ACCENT : "#1C1E2E", color: system === u.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
          >
            {u.label}
          </button>
        ))}
      </div>

      <label style={labelStyle}>Bolt grade</label>
      <select value={grade} onChange={(e) => setGrade(e.target.value)} style={sel}>
        {compatibleGrades.map((g) => <option key={g} value={g}>{GRADES[g].label}</option>)}
      </select>

      <label style={labelStyle}>Bolt size / thread</label>
      <select value={size} onChange={(e) => setSize(e.target.value)} style={sel}>
        {sizes.map((s) => <option key={s[0]} value={s[0]}>{s[0]}</option>)}
      </select>

      {result && (
        <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Readout label="Newton-meters" value={`${result.Nm.toFixed(result.Nm < 10 ? 1 : 0)} N·m`} />
            <Readout label="Foot-pounds" value={`${result.ftLb.toFixed(result.ftLb < 10 ? 1 : 0)} ft·lb`} />
          </div>
        </div>
      )}

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 16 }}>
        Computed with K=0.20 (plain dry threads). Lubricated threads need ~10-20% less torque. Anti-seize, lock washers, and joint material all change the right value — these are starting points, not specs.
      </p>
    </ToolsShell>
  );
}

function Readout({ label, value }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
